import { CustomField } from "@app.raytd.com/common/types/custom-field";

const formatNumber = (value: number | string | null | undefined, decimalPlaces: number): string | null => {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    if (isNaN(num)) return String(value); // Return original if not a number

    return new Intl.NumberFormat('en-AU', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(num);
};

const formatDate = (dateString: string | null | undefined, includeTime = false): string | null => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid

    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    };
    if (includeTime) {
        options.hour = 'numeric';
        options.minute = 'numeric';
        options.second = 'numeric';
    }
    return new Intl.DateTimeFormat('en-AU', options).format(date);
};

type CustomFieldDefinition = Pick<CustomField, 'type' | 'data' | 'selection_type' | 'decimal_places'>;
type AllValueReturnTypes = string | number | boolean | null;

export default function getCustomFieldDisplayValue(fieldDefinition: CustomFieldDefinition, rawValue: any): AllValueReturnTypes {
    if (rawValue === null || rawValue === undefined) return null;

    switch (fieldDefinition.type) {
        case 'textarea':
        case 'text':
            return String(rawValue).trim() || null;
        case 'select':
        case 'radio': {
            if (!fieldDefinition.data) return null;
            if (fieldDefinition.selection_type === 'multiple') {
                const values = (Array.isArray(rawValue) ? rawValue : [rawValue])
                    .map(val => fieldDefinition.data?.find(opt => opt.id === val)?.displayName)
                    .filter(Boolean);
                return values.length > 0 ? values.join(', ') : null;
            }
            const option = fieldDefinition.data?.find(opt => opt.id === rawValue);
            return option ? option.displayName : null;
        }
        case 'switch':
        case 'checkbox':
            // Return boolean value directly
            return Boolean(rawValue);

        case 'number':
            return formatNumber(rawValue, fieldDefinition.decimal_places ?? 0);

        case 'date':
            // Assuming 'date' type doesn't include time based on original component
            return formatDate(rawValue as string, false);

        default:
            return rawValue;
    }
}